import React from 'react';
import {
    DetailsIconsOptions,
    EmailSubsectionChip,
    IconAuditTrail,
    IconChipCircleTools,
    IconClockTable,
    IconDocumentPencil,
    IconEmployeeInside,
    IconExitDevice,
    IconGuardTitle,
    IconScheduleTable,
    IEntryPrimary,
    PersonChip,
    PhoneLight,
} from '../../../../components/buttons/IconButtons';
import ResumeInfoCard from '../../../../components/ResumeInfoCard';
import CustomModal from '../../../../components/Modal/CustomModal';
import CustomCard from '../../../../components/CustomCard';
import {
    DATE_TIME_FORMAT_NAME_MODEL,
    EMPTY_VALUES,
    getDateFormat,
    getTimeFormat,
} from '../../../../Apis/functions';
import CustomTooltip from '../../../../components/CustomTooltip';
import MaterialDetailsModalApp from '../Functionality/MaterialDetailsModalApp';
import { URL } from '../../../../Apis/Constants';
import {colors} from "../../../../enums/colors";
import CustomChip from "../../../../components/CustomChip";
import ButtonAction from '../../../../components/buttons/ButtonAction';

/**
 * @component MaterialDetailsModal
 * @description Detail of a material entry, opened from the equipment and material tab.
 *
 * @param {boolean} show - Whether the modal is open.
 * @param {Function} onHide - Closes the modal.
 * @param {Object} itemSelected - Row selected in the table, its id is the material access log id.
 * @param {Function} setCheckOutModal - Opens the exit of the material.
 * @param {string} display - Display classes, used to hide this modal while the exit is open.
 * @param {Function} t - Translation function.
 */
const MaterialDetailsModal = ({ show, onHide, itemSelected, setCheckOutModal, display, t }) => {
    const { detail, material, canCheckOut } = MaterialDetailsModalApp({ itemSelected });

    return (
        <CustomModal
            show={show}
            size='lg'
            onHide={onHide}
            display={display}
            confirmButton={false}
            cancelButton={false}
            topLeftHeader={
                <>
                    <div className={'d-flex align-items-center pb-3'}>
                        <DetailsIconsOptions className='icon-color-general-950' />
                        <p className={'fs-modal-title font-color-general-950 ibl-font-w-500 ml-2'}>{t('details')}</p>
                    </div>
                </>
            }
            classNameBody='pt-2'
        >
            <CustomCard classCard={''} classContent={'p-0'}>
                <IEntryPrimary className={'w-100'} />
                <div className={'row'}>
                    <div className={'col-12 my-4'}>
                        <div className={'d-flex align-items-center minw-0'}>
                            {material?.fileUploadDto ? (
                                <img
                                    className='h-32px w-32px border-radius-8px'
                                    src={URL + material.fileUploadDto.filePath}
                                    alt={material?.name}
                                />
                            ) : (
                                <IconChipCircleTools className='h-32px w-32px' />
                            )}
                            <CustomTooltip
                                text={material?.name || EMPTY_VALUES.DEFAULT}
                                classes={'font-color-general-950 fs-14px ml-2 text-ellipsis'}
                                showIfTruncated={true}
                                placement={'top'}
                            />
                            <div className={'d-flex justify-content-end ml-auto'}>
                                <CustomChip
                                    legend={material?.code}
                                    background={colors.secondary100}
                                    fontColor={colors.secondary700}
                                    borderColor={colors.secondary150}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className={'border-bottom-shades-300'} />
                <div className='m-3'>
                    <ResumeInfoCard
                        title={t('trail')}
                        titleIcon={<IconAuditTrail width={24} height={24} />}
                        titleActions={
                            canCheckOut && (
                                <ButtonAction
                                    id='btn_mdm_check_out'
                                    name='check_out'
                                    className='btn-outlined-secondary-600-25'
                                    Icon={IconExitDevice}
                                    splitName={false}
                                    onClick={() => setCheckOutModal(true)}
                                />
                            )
                        }
                        icon={<IconEmployeeInside className='btn-icon-svg-24px' />}
                        iconClass='background-primary-100'
                        contentClass={'py-3'}
                        body={
                            <>
                                <div className='d-flex flex-column'>
                                    <div className='d-flex flex-column'>
                                        <p className='font-color-general-950 fs-12px ibl-font-w-600'>{t('check_in_log')}</p>

                                        <div className='row mt-1 '>
                                            <div className='col-sm-12 col-md-6 d-flex align-items-center mx-0 p-0 '>
                                                <IconScheduleTable className='icon-color-general-950' />
                                                <p className='font-color-general-950 fs-14px ml-2 mb-0'>
                                                    {getDateFormat(
                                                        detail?.accessDate,
                                                        false,
                                                        DATE_TIME_FORMAT_NAME_MODEL.shortMonth,
                                                    )}
                                                </p>
                                            </div>

                                            <div className='col-sm-12 col-md-6 d-flex align-items-center mx-0 p-0'>
                                                <IconClockTable className='icon-color-general-950' />
                                                <p className='font-color-general-950 fs-14px ml-2 mb-0'>
                                                    {getTimeFormat(detail?.accessDate)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className='row my-3 mx-0'>
                                        <div className='col-sm-12 col-md-6 d-flex flex-column mx-0 p-0'>
                                            <p className='font-color-general-950 fs-12px ibl-font-w-600'>
                                                {t('guard')}
                                            </p>

                                            <div className='d-flex align-items-center'>
                                                <IconGuardTitle className='icon-color-general-950 h-16px w-16px' />
                                                <p className='font-color-general-950 fs-14px ml-2 mb-0'>
                                                    {detail?.guardFullName || EMPTY_VALUES.DEFAULT}
                                                </p>
                                            </div>
                                        </div>

                                        <div className='col-sm-12 col-md-6 d-flex flex-column mx-0 p-0'>
                                            <p className='font-color-general-950 fs-12px ibl-font-w-600'>
                                                {t('contractor')}
                                            </p>

                                            <div className='d-flex align-items-center'>
                                                <IconDocumentPencil className='icon-color-general-950 h-16px w-16px' />
                                                <p className='font-color-general-950 fs-14px ml-2 mb-0'>
                                                    {detail?.contractorName || EMPTY_VALUES.DEFAULT}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className={'border-bottom-shades-300'} />
                                <p className={'font-color-general-950 fs-12px ibl-font-w-600 mt-3 mb-2'}>
                                    {t('entered_by')}
                                </p>
                                <div className='border-full-shades-200 background-shades-100 border-radius-8px'>
                                    <div className='row align-items-center my-1'>
                                        <div className='col'>
                                            <div className='d-flex align-items-center'>
                                                {detail?.image ? (
                                                    <img
                                                        className='h-32px w-32px object-fit-cover rounded-circle'
                                                        src={`data:image/png;base64,${detail.image}`}
                                                        alt={detail?.fullName}
                                                    />
                                                ) : (
                                                    <PersonChip className='h-32px w-32px' />
                                                )}
                                                <CustomTooltip
                                                    text={detail?.fullName || EMPTY_VALUES.DEFAULT}
                                                    classes={`font-color-general-950 ibl-font-w-600 fs-12px ml-2 text-ellipsis`}
                                                    showIfTruncated={true}
                                                />
                                            </div>
                                        </div>

                                        <div className='col-auto'>
                                            <div className='d-flex align-items-center'>
                                                <EmailSubsectionChip className='h-16px w-16px icon-color-shades-600' />
                                                <span className='font-color-shades-600 ibl-font-w-400 fs-12px ml-2'>
                                                    {detail?.email || EMPTY_VALUES.DEFAULT}
                                                </span>
                                            </div>
                                        </div>

                                        <div className='col-auto'>
                                            <div className='d-flex align-items-center ms-4'>
                                                <PhoneLight className='h-16px w-16px icon-color-shades-600' />
                                                <span className='font-color-shades-600 ibl-font-w-400 fs-12px ml-2'>
                                                    {detail?.phoneNumber || EMPTY_VALUES.DEFAULT}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        }
                    />
                </div>
            </CustomCard>
        </CustomModal>
    );
};

export default MaterialDetailsModal;
